<template>
  <BaseModal
    :modalTitle="$t('area.customer-account.users.invite.title')"
    :modalIntro="$t('area.customer-account.users.invite.intro')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <UiInputTextAreaVee
        name="emails"
        :label="$t('form.label.email-addresses')"
        :placeholder="$t('form.placeholder.email-addresses')"
        v-model="emailsText"
        @update:model-value="resetValidationMessages" />

      <button
        v-if="EMU.isEmulatingFirestore"
        type="button"
        class="-mt-2 mb-2 flex w-fit cursor-pointer items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        @click="addFakeEmail">
        <IconLucideSparkles class="h-4 w-4" />
        {{ $t('W: Tilføj dummy-email') }}
      </button>

      <span class="text-sm text-text-primary">
        {{ $t('area.customer-account.users.invite.email-count') }}:
        {{ validEmails.length }}
      </span>

      <InfoText
        :text="validationText"
        class="mt-2 mb-4"
        theme="warning" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.send-invites')"
          type="submit"
          :isDisabled="validEmails.length === 0"
          :isLoading="isLoading" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import { EMU } from '@/globals/typescript/utils/host'
import { useToast } from '@/globals/typescript/utils/toast'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import { union } from 'lodash-es'
import { mapActions, mapState } from 'pinia'
import isEmail from 'validator/lib/isEmail'
import type { PropType } from 'vue'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import { customerKey } from '@/globals/typescript/utils/customerOwner'

export default {
  name: 'InviteCustomerUserBusinessModal',

  // A business or an insurance customer (MIL-402) — the API path follows `customer`.
  props: {
    customer: { type: Object as PropType<ICustomerRef>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      emailsText: '',
      errorText: '',
      isLoading: false,

      EMU,
    }
  },

  computed: {
    ...mapState(useCustomerUserStore, ['customerUsersByCustomerKey']),

    emailList(): string[] {
      return union(
        this.emailsText
          .split(',')
          .map((email) => email.replace(/\s+/g, ''))
          .filter(
            (email) =>
              email.length > 0 &&
              isEmail(email, { allow_utf8_local_part: true })
          )
      )
    },

    existingEmails(): string[] {
      const users =
        this.customerUsersByCustomerKey[customerKey(this.customer)] ?? []
      return users
        .filter((x) => this.emailList.includes(x.email))
        .map((x) => x.email)
    },

    validEmails(): string[] {
      return this.emailList.filter(
        (email) => !this.existingEmails.includes(email)
      )
    },

    validationText(): string {
      if (this.existingEmails.length) {
        return `${this.$t(
          'area.customer-account.users.invite.message.existing-emails'
        )}: <br> ${this.existingEmails.join(', ')}`
      }
      return ''
    },
  },

  methods: {
    ...mapActions(useCustomerUserStore, ['inviteCustomerUsers']),
    ...mapActions(useModalStore, ['closeTop']),

    addFakeEmail() {
      const email = generateFakeEmail()
      this.emailsText = this.emailsText.trim()
        ? `${this.emailsText.trim()}, ${email}`
        : email
      this.resetValidationMessages()
    },

    resetValidationMessages() {
      this.errorText = ''
    },

    async onSubmit(): Promise<void> {
      if (this.validEmails.length === 0) return

      this.resetValidationMessages()
      this.isLoading = true

      try {
        await this.inviteCustomerUsers(this.customer, this.validEmails)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('area.customer-account.users.invite.success'))
        this.closeTop()
      } catch (error) {
        reportUnexpectedError(error, { operation: 'customer-user-invite' })
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },

    formResetCallback(): boolean {
      if (this.errorText) {
        return false
      }
      this.emailsText = ''
      return true
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ emailsText: this.emailsText }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

